import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useCouponAuth, AuthError } from '../hooks/use-coupon-auth';
import { ForgotPasswordModal } from './ForgotPasswordModal';
import { IconLoader4, IconLock, IconMail } from '@tabler/icons-react';

interface SignInFormProps {
  onSuccess: () => void;
  onCreateAccount: () => void;
  submitLabel?: string;
}

export const SignInForm = ({ onSuccess, onCreateAccount, submitLabel = 'Sign In' }: SignInFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);

  const { signIn } = useCouponAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!password) {
      setError('Please enter your password');
      return;
    }

    try {
      await signIn.mutateAsync({ email: email.trim(), password });
      onSuccess();
    } catch (err: unknown) {
      if (err instanceof AuthError) {
        setError(err.message);
      } else {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('401') || msg.includes('403') || msg.includes('Invalid')) {
          setError('Invalid email or password.');
        } else {
          setError('Unable to sign in. Please try again.');
        }
      }
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="signin-email">Email</Label>
          <div className="relative">
            <IconMail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="signin-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10"
              autoComplete="email"
              required
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="signin-password">Password</Label>
          <div className="relative">
            <IconLock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="signin-password"
              type="password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10"
              autoComplete="current-password"
              required
            />
          </div>
        </div>

        <Button type="submit" disabled={signIn.isPending} className="w-full">
          {signIn.isPending ? (
            <>
              <IconLoader4 className="h-4 w-4 mr-2 animate-spin" />
              Signing in...
            </>
          ) : (
            submitLabel
          )}
        </Button>

        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => setForgotPasswordOpen(true)}
            className="text-primary hover:underline"
          >
            Forgot Password?
          </button>
          <button
            type="button"
            onClick={onCreateAccount}
            className="text-primary hover:underline"
          >
            Create Account
          </button>
        </div>
      </form>

      <ForgotPasswordModal
        open={forgotPasswordOpen}
        onOpenChange={setForgotPasswordOpen}
      />
    </>
  );
};
